'use client'

import { map, tail } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { readRemoteFile } from 'react-papaparse'

import CircularProgress from '@mui/material/CircularProgress'

export type CsvViewerProps = {
  onError?: (error?: unknown) => void
  src: string
}

export default function CsvViewer({ onError, src }: CsvViewerProps) {
  const [data, setData] = useState<string[][]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error>()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const columns: { id: string; label: string }[] = useMemo(() => {
    return map(data[0], (field, index) => ({
      id: field + index,
      label: field,
    }))
  }, [data])

  const rows = useMemo(() => {
    return tail(data)
  }, [data])

  useEffect(() => {
    readRemoteFile<string[]>(src, {
      complete: (results) => {
        setData(results.data)
        setLoading(false)
      },
      error: (error) => {
        setLoading(false)
        setError(error)
        onError?.(error)
      },
      download: true,
    })
    // eslint-disable-next-line
  }, [src])

  if (loading) {
    return <CircularProgress />
  }

  return (
    <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
      <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
        <tr>
          {columns.map((column) => (
            <th key={column.id} scope="col" className="px-6 py-3">
              {column.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, rowIdx) => {
          return (
            <tr key={rowIdx} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
              {row.map((cell: string, cellIdx) => {
                return (
                  <th key={cellIdx} scope="row" className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                    {cell}
                  </th>
                )
              })}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
